import {
  AVATARS,
  DEFAULT_SETTINGS,
  DEFAULT_TEAM_NAMES,
  MAX_PLAYERS,
  MIN_PLAYERS,
  NAME_MAX,
  SETTING_LIMITS,
  WRITE_IN_MAX,
  checkWrittenPrompt,
  cleanText,
  isEveryNth,
} from './rules';
import type { CardData, Phase, RoomSettings, RoundRecap, SubmitChoice } from './types';

/** How long a disconnected judge holds up a round before it is skipped. */
export const JUDGE_AWAY_MS = 30_000;
/** How long a disconnected host keeps the crown before it passes to someone present. */
export const HOST_AWAY_MS = 60_000;
const RECAP_LIMIT = 30;
/** With the round timer on, how long the judge gets to choose a prompt. */
export const CHOOSE_SECONDS = 20;
/** With the round timer on, how long a player gets to write a prompt. */
export const WRITE_SECONDS = 60;
/** With the round timer on, how long everyone gets to guess who played the winner. */
export const GUESS_SECONDS = 30;
/** Phases where a round is in progress and cards or prompts are still in play. */
const ACTIVE_PHASES: readonly Phase[] = ['choosing', 'writing', 'submitting', 'judging', 'guessing'];

export class GameError extends Error {}

export interface PlayerState {
  id: string;
  token: string;
  name: string;
  avatar: string;
  score: number;
  connected: boolean;
  joinedAt: number;
  lastSeenAt: number;
  hand: CardData[];
  writeInsLeft: number;
  redrawsLeft: number;
  /** Signed-in account behind this seat, for game history. */
  userId?: number | null;
  /** Team index when teams are on. */
  team?: number | null;
}

export interface SubmissionState {
  id: string;
  playerId: string;
  playerName: string;
  cards: CardData[];
  revealed: boolean;
  /** Voter player ids (vote mode). */
  votes: string[];
}

export interface RoundState {
  number: number;
  judgeId: string | null;
  prompt: CardData;
  submissions: SubmissionState[];
  deadline: number | null;
  winnerSubmissionIds: string[];
  notice: string | null;
  // Game modes. Optional so rooms saved before these existed still load (see normalizeState).
  /** Sabse Bekaar round: the worst answer wins. */
  worst?: boolean;
  /** The player writing (or who wrote) this round's prompt. */
  writerId?: string | null;
  /** Choosing phase: prompts the judge picks between (round.prompt is the fallback). */
  promptOptions?: CardData[] | null;
  /** Guessing phase: who may guess, and each guess (guesser id -> suspect id). */
  guessers?: string[];
  guesses?: Record<string, string>;
  /** Set once the guesses have been scored and revealed. */
  guessesRevealed?: boolean;
}

export interface DeckState {
  prompts: string[];
  answers: string[];
  promptDiscard: string[];
  answerDiscard: string[];
}

export interface RoomState {
  code: string;
  createdAt: number;
  updatedAt: number;
  hostId: string | null;
  phase: Phase;
  settings: RoomSettings;
  players: PlayerState[];
  round: RoundState | null;
  deck: DeckState | null;
  recap: RoundRecap[];
  gameId: number | null;
  roundsPlayed: number;
  kickedTokens: string[];
  /** Team totals, parallel to settings.teams. */
  teamScores?: number[];
  /** Vote mode: the last player who wrote a prompt, so the pen rotates. */
  lastWriterId?: string | null;
  /** One-phone pass-and-play: every seat is played from one device holding `ownerToken`. */
  onePhone?: boolean;
  ownerToken?: string;
}

/** Side effects the room manager carries out after an engine call (stats, suggestions). */
export type GameEvent =
  | { type: 'game_started'; playerCount: number }
  | { type: 'round_finished'; playedCardIds: string[]; winningCardIds: string[] }
  | { type: 'game_over'; winnerName: string | null }
  | {
      type: 'write_in';
      text: string;
      playerName: string;
      suggest: boolean;
      promptText: string;
      /** 'prompt' for a prompt written in a write-your-own round. Missing means an answer card. */
      cardType?: 'answer' | 'prompt';
      pick?: number;
    };

export interface EngineDeps {
  /** Returns the card for a deck id, or undefined if it was deleted. */
  lookup: (id: string) => CardData | undefined;
  random?: () => number;
  now?: () => number;
  newId?: () => string;
}

export function createRoomState(code: string, settings: RoomSettings, now: number): RoomState {
  return {
    code,
    createdAt: now,
    updatedAt: now,
    hostId: null,
    phase: 'lobby',
    settings,
    players: [],
    round: null,
    deck: null,
    recap: [],
    gameId: null,
    roundsPlayed: 0,
    kickedTokens: [],
    teamScores: [],
    lastWriterId: null,
  };
}

export function defaultSettings(packIds: string[]): RoomSettings {
  return { ...DEFAULT_SETTINGS, teams: [], packIds };
}

/**
 * Fill in fields added after a room was saved, so old snapshots keep working. Safe to call on
 * any state; it only adds what is missing.
 */
export function normalizeState(state: RoomState): RoomState {
  state.settings = sanitizeSettings({ ...DEFAULT_SETTINGS, ...state.settings });
  const teamCount = state.settings.teams?.length ?? 0;
  state.teamScores = Array.from({ length: teamCount }, (_, i) => state.teamScores?.[i] ?? 0);
  state.lastWriterId ??= null;
  state.kickedTokens ??= [];
  state.recap ??= [];
  for (const p of state.players) {
    if (p.team === undefined || (p.team !== null && (p.team < 0 || p.team >= teamCount))) p.team = null;
  }
  const round = state.round;
  if (round) {
    round.worst ??= false;
    round.writerId ??= null;
    round.promptOptions ??= null;
    round.guessers ??= [];
    round.guesses ??= {};
    round.guessesRevealed ??= false;
    round.notice ??= null;
    round.winnerSubmissionIds ??= [];
  }
  return state;
}

export function teamsOn(settings: RoomSettings): boolean {
  return (settings.teams?.length ?? 0) >= SETTING_LIMITS.teams.min;
}

/** Players (or, with teams on, every member of the leading teams) who are winning right now. */
export function leaderIds(state: RoomState): string[] {
  if (teamsOn(state.settings)) {
    const scores = state.teamScores ?? [];
    const top = Math.max(0, ...scores);
    if (top === 0) return [];
    return state.players.filter((p) => p.team != null && scores[p.team] === top).map((p) => p.id);
  }
  const top = Math.max(0, ...state.players.map((p) => p.score));
  return top === 0 ? [] : state.players.filter((p) => p.score === top).map((p) => p.id);
}

/**
 * The rules of the game as a state machine over a plain, JSON-serialisable RoomState.
 * Methods throw GameError for anything a player did wrong; the caller reports the message.
 */
export class Game {
  readonly events: GameEvent[] = [];
  private readonly random: () => number;
  private readonly now: () => number;
  private readonly newId: () => string;

  constructor(public state: RoomState, private readonly deps: EngineDeps) {
    normalizeState(state);
    this.random = deps.random ?? Math.random;
    this.now = deps.now ?? Date.now;
    this.newId = deps.newId ?? (() => Math.random().toString(36).slice(2, 10));
  }

  drainEvents(): GameEvent[] {
    return this.events.splice(0, this.events.length);
  }

  // ---------------------------------------------------------------- players

  player(id: string): PlayerState {
    const p = this.state.players.find((x) => x.id === id);
    if (!p) throw new GameError('You are not in this room.');
    return p;
  }

  findByToken(token: string): PlayerState | undefined {
    return this.state.players.find((p) => p.token === token);
  }

  connectedPlayers(): PlayerState[] {
    return this.state.players.filter((p) => p.connected);
  }

  addPlayer(input: { name: string; avatar?: string; token: string; userId?: number | null }): PlayerState {
    const s = this.state;
    if (s.kickedTokens.includes(input.token)) throw new GameError('You were removed from this room.');
    const name = validateName(input.name);
    if (s.players.length >= MAX_PLAYERS) throw new GameError(`This room is full (${MAX_PLAYERS} players).`);
    if (s.players.some((p) => p.name.toLowerCase() === name.toLowerCase())) {
      throw new GameError('Someone in this room already has that name.');
    }
    const now = this.now();
    const taken = new Set(s.players.map((p) => p.avatar));
    const avatar =
      input.avatar && AVATARS.includes(input.avatar) && !taken.has(input.avatar)
        ? input.avatar
        : (AVATARS.find((a) => !taken.has(a)) ?? AVATARS[0]);
    const player: PlayerState = {
      id: this.newId(),
      token: input.token,
      name,
      avatar,
      score: 0,
      connected: true,
      joinedAt: now,
      lastSeenAt: now,
      hand: [],
      writeInsLeft: s.settings.writeIns,
      redrawsLeft: s.settings.redraws,
      userId: input.userId ?? null,
      team: null,
    };
    s.players.push(player);
    if (teamsOn(s.settings)) player.team = this.smallestTeam(player.id);
    if (!s.hostId) s.hostId = player.id;
    if (s.phase !== 'lobby' && s.phase !== 'game_over') this.refillHand(player);
    this.touch();
    return player;
  }

  setAvatar(playerId: string, avatar: string): void {
    const p = this.player(playerId);
    if (!AVATARS.includes(avatar)) throw new GameError('Unknown avatar.');
    if (this.state.players.some((x) => x.id !== playerId && x.avatar === avatar)) {
      throw new GameError('Someone already picked that one.');
    }
    p.avatar = avatar;
    this.touch();
  }

  setConnected(playerId: string, connected: boolean): void {
    const p = this.state.players.find((x) => x.id === playerId);
    if (!p) return;
    p.connected = connected;
    p.lastSeenAt = this.now();
    if (!this.state.players.some((x) => x.id === this.state.hostId)) this.state.hostId = p.id;
    this.advanceIfReady();
    this.touch();
  }

  /** Voluntary leave or a kick. */
  removePlayer(playerId: string, opts: { kicked?: boolean } = {}): void {
    const s = this.state;
    const idx = s.players.findIndex((p) => p.id === playerId);
    if (idx === -1) return;
    const [player] = s.players.splice(idx, 1);
    if (opts.kicked) s.kickedTokens.push(player.token);
    this.discardAnswers(player.hand);
    const round = s.round;
    if (round && ACTIVE_PHASES.includes(s.phase)) {
      if (round.judgeId === playerId && s.phase !== 'guessing') {
        this.skipRound(`${player.name} (the judge) left, so here's a fresh round.`);
      } else if (s.phase === 'writing' && round.writerId === playerId) {
        this.useDealtPrompt(`${player.name} left before writing, so here's a card from the deck.`);
      } else {
        const sub = round.submissions.find((x) => x.playerId === playerId);
        if (sub && s.phase === 'submitting') {
          round.submissions = round.submissions.filter((x) => x !== sub);
          this.discardAnswers(sub.cards);
        }
        for (const x of round.submissions) x.votes = x.votes.filter((v) => v !== playerId);
        if (round.guesses) delete round.guesses[playerId];
      }
    }
    if (s.hostId === playerId) {
      s.hostId = null;
      this.promoteHostIfAway();
    }
    this.advanceIfReady();
    this.touch();
  }

  transferHost(requesterId: string, targetId: string): void {
    this.requireHost(requesterId);
    const target = this.player(targetId);
    this.state.hostId = target.id;
    this.touch();
  }

  // ---------------------------------------------------------------- settings

  updateSettings(requesterId: string, patch: Partial<RoomSettings>): void {
    this.requireHost(requesterId);
    if (this.state.phase !== 'lobby' && this.state.phase !== 'game_over') {
      throw new GameError('Settings can only change between games.');
    }
    this.state.settings = sanitizeSettings({ ...this.state.settings, ...patch });
    for (const p of this.state.players) {
      p.writeInsLeft = this.state.settings.writeIns;
      p.redrawsLeft = this.state.settings.redraws;
    }
    this.assignTeams();
    this.touch();
  }

  /** Join a team (anyone, for themselves) or move another player (host only). Between games only. */
  setTeam(requesterId: string, targetId: string, team: number): void {
    const s = this.state;
    if (!teamsOn(s.settings)) throw new GameError('Teams are off in this room.');
    if (s.phase !== 'lobby' && s.phase !== 'game_over') throw new GameError('Teams can only change between games.');
    if (requesterId !== targetId) this.requireHost(requesterId);
    const target = this.player(targetId);
    const index = Number(team);
    if (!Number.isInteger(index) || index < 0 || index >= s.settings.teams!.length) throw new GameError('No such team.');
    target.team = index;
    this.touch();
  }

  // ---------------------------------------------------------------- game lifecycle

  /**
   * Start a game. `cards` is every active card in the selected packs; the deck keeps only ids.
   */
  start(requesterId: string, cards: CardData[]): void {
    const s = this.state;
    this.requireHost(requesterId);
    if (s.phase !== 'lobby' && s.phase !== 'game_over') throw new GameError('A game is already running.');
    const connected = this.connectedPlayers();
    if (connected.length < MIN_PLAYERS) throw new GameError(`You need at least ${MIN_PLAYERS} players to start.`);
    const prompts = cards.filter((c) => c.type === 'prompt').map((c) => c.id);
    const answers = cards.filter((c) => c.type === 'answer').map((c) => c.id);
    if (prompts.length === 0) throw new GameError('The selected packs have no prompt cards.');
    const needed = s.players.length * s.settings.handSize + 3 * s.players.length;
    if (answers.length < needed) {
      throw new GameError(`The selected packs need at least ${needed} answer cards for this many players. Add another pack.`);
    }
    s.deck = {
      prompts: this.shuffle(prompts),
      answers: this.shuffle(answers),
      promptDiscard: [],
      answerDiscard: [],
    };
    if (teamsOn(s.settings)) {
      this.assignTeams();
      const empty = s.settings.teams!.find((_, i) => !connected.some((p) => p.team === i));
      if (empty !== undefined) throw new GameError(`Team ${empty} needs at least one player.`);
    }
    for (const p of s.players) {
      p.score = 0;
      p.hand = [];
      p.writeInsLeft = s.settings.writeIns;
      p.redrawsLeft = s.settings.redraws;
    }
    s.teamScores = (s.settings.teams ?? []).map(() => 0);
    s.lastWriterId = null;
    s.recap = [];
    s.roundsPlayed = 0;
    s.round = null;
    this.events.push({ type: 'game_started', playerCount: s.players.length });
    this.startRound();
  }

  /** Host ends the game early; the leader wins. */
  end(requesterId: string): void {
    this.requireHost(requesterId);
    if (this.state.phase === 'lobby' || this.state.phase === 'game_over') throw new GameError('No game is running.');
    if (this.state.phase === 'guessing') {
      // The winner was already picked: score it before wrapping up.
      this.settleRound();
      if ((this.state.phase as Phase) === 'game_over') return;
    }
    if (ACTIVE_PHASES.includes(this.state.phase)) this.returnSubmissions();
    this.finishGame();
  }

  /** Back to the lobby with the same players and settings. */
  restart(requesterId: string): void {
    this.requireHost(requesterId);
    const s = this.state;
    if (s.phase !== 'game_over') throw new GameError('Finish this game first.');
    s.phase = 'lobby';
    s.round = null;
    s.deck = null;
    s.gameId = null;
    for (const p of s.players) {
      p.score = 0;
      p.hand = [];
    }
    s.teamScores = (s.settings.teams ?? []).map(() => 0);
    this.touch();
  }

  next(requesterId: string): void {
    const s = this.state;
    if (s.phase !== 'round_over') throw new GameError('The round is not over yet.');
    const isJudge = s.round?.judgeId === requesterId;
    if (requesterId !== s.hostId && !isJudge && s.settings.judgeMode === 'judge') {
      throw new GameError('Only the judge or the host can start the next round.');
    }
    this.startRound();
  }

  /** Host skips a stuck round. Played cards go back to their owners. */
  skip(requesterId: string): void {
    this.requireHost(requesterId);
    if (!ACTIVE_PHASES.includes(this.state.phase)) throw new GameError('There is no round to skip.');
    if (this.state.phase === 'guessing') {
      // The winner is already picked, so skipping the guessing just reveals it.
      this.settleRound();
      return;
    }
    this.skipRound('The host skipped that one.');
  }

  // ---------------------------------------------------------------- mode actions

  /** Prompt choice: the judge picks which prompt to play. */
  choosePrompt(playerId: string, promptId: string): void {
    const s = this.state;
    const round = s.round;
    if (s.phase !== 'choosing' || !round?.promptOptions) throw new GameError('It is not time to choose a prompt.');
    if (round.judgeId !== playerId) throw new GameError('Only the judge picks the prompt.');
    const chosen = round.promptOptions.find((c) => c.id === promptId);
    if (!chosen) throw new GameError('That prompt is not one of the choices.');
    for (const c of round.promptOptions) if (c.id !== chosen.id) this.discardPrompt(c);
    round.prompt = chosen;
    round.promptOptions = null;
    this.beginSubmitting();
    this.touch();
  }

  /** Write-your-own round: the writer's prompt replaces the dealt one. */
  writePrompt(playerId: string, text: string, opts: { suggest?: boolean } = {}): void {
    const round = this.requireWriter(playerId);
    const checked = checkWrittenPrompt(text);
    if (!checked.ok) throw new GameError(checked.error);
    const writer = this.player(playerId);
    this.discardPrompt(round.prompt);
    round.prompt = { id: `wp_${this.newId()}`, type: 'prompt', text: checked.text, pick: checked.pick, writeIn: true };
    this.events.push({
      type: 'write_in',
      text: checked.text,
      playerName: writer.name,
      suggest: Boolean(opts.suggest),
      promptText: checked.text,
      cardType: 'prompt',
      pick: checked.pick,
    });
    this.beginSubmitting();
    this.touch();
  }

  /** Write-your-own round: the writer passes and plays the dealt prompt instead. */
  useDeckPrompt(playerId: string): void {
    this.requireWriter(playerId);
    this.useDealtPrompt(null);
  }

  /** Guess who: guess which player played the winning card. One guess each, no changes. */
  guess(playerId: string, suspectId: string): void {
    const s = this.state;
    const round = s.round;
    if (s.phase !== 'guessing' || !round) throw new GameError('It is not time to guess.');
    this.player(playerId);
    if (!round.guessers?.includes(playerId)) throw new GameError('You sit this guess out.');
    const guesses = (round.guesses ??= {});
    if (guesses[playerId]) throw new GameError('You already guessed.');
    if (suspectId === playerId) throw new GameError('You know it was not you.');
    if (!round.submissions.some((x) => x.playerId === suspectId)) throw new GameError('That player did not play a card this round.');
    guesses[playerId] = suspectId;
    this.advanceIfReady();
    this.touch();
  }

  // ---------------------------------------------------------------- round actions

  submit(playerId: string, choices: SubmitChoice[], opts: { suggestWriteIn?: boolean } = {}): void {
    const s = this.state;
    const round = s.round;
    if (s.phase !== 'submitting' || !round) throw new GameError('It is not time to play cards.');
    const player = this.player(playerId);
    if (round.judgeId === playerId) throw new GameError('The judge sits this one out.');
    if (round.submissions.some((x) => x.playerId === playerId)) throw new GameError('You already played this round.');
    if (!Array.isArray(choices) || choices.length !== round.prompt.pick) {
      throw new GameError(`Pick exactly ${round.prompt.pick} card${round.prompt.pick > 1 ? 's' : ''}.`);
    }
    const handIds = new Set<string>();
    const writeIns = choices.filter((c): c is { writeIn: string } => 'writeIn' in c);
    if (writeIns.length > player.writeInsLeft) throw new GameError('You are out of blank cards.');
    const cards: CardData[] = choices.map((choice) => {
      if ('writeIn' in choice) {
        const text = cleanText(String(choice.writeIn ?? ''));
        if (!text) throw new GameError('Write something on your blank card first.');
        if (text.length > WRITE_IN_MAX) throw new GameError(`Keep blank cards under ${WRITE_IN_MAX} characters.`);
        return { id: `w_${this.newId()}`, type: 'answer', text, pick: 1, writeIn: true };
      }
      const card = player.hand.find((c) => c.id === choice.cardId);
      if (!card || handIds.has(card.id)) throw new GameError('That card is not in your hand.');
      handIds.add(card.id);
      return card;
    });
    player.hand = player.hand.filter((c) => !handIds.has(c.id));
    player.writeInsLeft -= writeIns.length;
    for (const card of cards.filter((c) => c.writeIn)) {
      this.events.push({
        type: 'write_in',
        text: card.text,
        playerName: player.name,
        suggest: Boolean(opts.suggestWriteIn),
        promptText: round.prompt.text,
      });
    }
    round.submissions.push({
      id: this.newId(),
      playerId,
      playerName: player.name,
      cards,
      revealed: false,
      votes: [],
    });
    this.advanceIfReady();
    this.touch();
  }

  redraw(playerId: string): void {
    const s = this.state;
    const player = this.player(playerId);
    if (s.phase !== 'submitting' || !s.round) throw new GameError('You can only swap your hand while cards are being played.');
    if (s.round.submissions.some((x) => x.playerId === playerId)) throw new GameError('You already played this round.');
    if (player.redrawsLeft <= 0) throw new GameError('No hand swaps left this game.');
    player.redrawsLeft -= 1;
    this.discardAnswers(player.hand);
    player.hand = [];
    this.refillHand(player);
    this.touch();
  }

  reveal(playerId: string, submissionId: string): void {
    const round = this.requireJudging(playerId);
    const sub = round.submissions.find((x) => x.id === submissionId);
    if (!sub) throw new GameError('That card is gone.');
    sub.revealed = true;
    this.touch();
  }

  pick(playerId: string, submissionId: string): void {
    const round = this.requireJudging(playerId);
    const sub = round.submissions.find((x) => x.id === submissionId);
    if (!sub) throw new GameError('That card is gone.');
    if (!sub.revealed) throw new GameError('Flip the card over before you pick it.');
    this.finishRound([sub.id]);
  }

  vote(playerId: string, submissionId: string): void {
    const s = this.state;
    const round = s.round;
    if (s.phase !== 'judging' || !round || s.settings.judgeMode !== 'vote') throw new GameError('It is not time to vote.');
    this.player(playerId);
    const sub = round.submissions.find((x) => x.id === submissionId);
    if (!sub) throw new GameError('That card is gone.');
    if (sub.playerId === playerId) throw new GameError('Nice try. You cannot vote for your own card.');
    if (!this.canVoteFor(playerId, sub)) throw new GameError('No voting for your own team.');
    for (const x of round.submissions) x.votes = x.votes.filter((v) => v !== playerId);
    sub.votes.push(playerId);
    this.advanceIfReady();
    this.touch();
  }

  /** Called every second by the room manager: timers and absent judges. */
  tick(): boolean {
    const s = this.state;
    const now = this.now();
    let changed = false;
    const host = s.players.find((p) => p.id === s.hostId);
    if (host && !host.connected && now - host.lastSeenAt > HOST_AWAY_MS) {
      const before = s.hostId;
      this.promoteHostIfAway();
      changed = s.hostId !== before;
      if (changed) this.touch();
    }
    const round = s.round;
    if (!round || !ACTIVE_PHASES.includes(s.phase)) return changed;
    /** The player if they are gone or have been offline too long, else undefined. */
    const away = (id: string | null | undefined): PlayerState | null | undefined => {
      const p = s.players.find((x) => x.id === id);
      if (!p) return null;
      return !p.connected && now - p.lastSeenAt > JUDGE_AWAY_MS ? p : undefined;
    };
    if (s.settings.judgeMode === 'judge' && round.judgeId && s.phase !== 'guessing') {
      const judge = away(round.judgeId);
      if (judge !== undefined) {
        this.skipRound(`${judge?.name ?? 'The judge'} dropped off, so here's a fresh round.`);
        return true;
      }
    }
    if (s.phase === 'writing') {
      const writer = away(round.writerId);
      if (writer !== undefined) {
        this.useDealtPrompt(`${writer?.name ?? 'The writer'} dropped off, so here's a card from the deck.`);
        return true;
      }
    }
    if (round.deadline && now >= round.deadline) {
      if (s.phase === 'choosing') this.choosePrompt(round.judgeId!, round.prompt.id);
      else if (s.phase === 'writing') this.useDealtPrompt('Time ran out, so here is a card from the deck.');
      else if (s.phase === 'submitting') this.toJudging();
      else if (s.phase === 'judging') this.tallyVotes();
      else this.settleRound();
      return true;
    }
    return changed;
  }

  // ---------------------------------------------------------------- internals

  /** Deal a new prompt. The judge seat moves on unless `sameJudge` names a present player. */
  private startRound(sameJudge?: string | null): void {
    const s = this.state;
    if (!s.deck) throw new GameError('No game is running.');
    if (this.connectedPlayers().length < MIN_PLAYERS) {
      throw new GameError(`Waiting for at least ${MIN_PLAYERS} connected players.`);
    }
    const prompt = this.drawPrompt();
    if (!prompt) throw new GameError('Out of prompt cards.');
    const previousJudge = s.round?.judgeId ?? null;
    const keep = sameJudge ? s.players.find((p) => p.id === sameJudge && p.connected) : undefined;
    for (const p of s.players) this.refillHand(p);
    const number = s.roundsPlayed + 1;
    const judging = s.settings.judgeMode === 'judge';
    const judgeId = judging ? (keep?.id ?? this.nextJudge(previousJudge)) : null;
    // Write-your-own: the judge writes; in vote mode the pen goes round the table.
    let writerId: string | null = null;
    if (isEveryNth(s.settings.writeOwnEvery, number)) {
      writerId = judging ? judgeId : this.nextJudge(s.lastWriterId ?? null);
      s.lastWriterId = writerId;
    }
    // Prompt choice (judge mode only): a second prompt to pick from, if the deck has one.
    let promptOptions: CardData[] | null = null;
    if (!writerId && judging && s.settings.promptChoice) {
      const second = this.drawPrompt();
      if (second) promptOptions = [prompt, second];
    }
    s.round = {
      number,
      judgeId,
      prompt,
      submissions: [],
      deadline: null,
      winnerSubmissionIds: [],
      notice: null,
      worst: isEveryNth(s.settings.worstEvery, number),
      writerId,
      promptOptions,
      guessers: [],
      guesses: {},
      guessesRevealed: false,
    };
    if (writerId) {
      s.phase = 'writing';
      s.round.deadline = this.timerDeadline(WRITE_SECONDS);
    } else if (promptOptions) {
      s.phase = 'choosing';
      s.round.deadline = this.timerDeadline(CHOOSE_SECONDS);
    } else {
      this.beginSubmitting();
    }
    this.touch();
  }

  private beginSubmitting(): void {
    const s = this.state;
    s.phase = 'submitting';
    s.round!.deadline = this.timerDeadline(s.settings.roundTimer);
  }

  /** A deadline `seconds` from now when the round timer is on, else null. */
  private timerDeadline(seconds: number): number | null {
    return this.state.settings.roundTimer > 0 ? this.now() + seconds * 1000 : null;
  }

  private drawPrompt(): CardData | undefined {
    const id = this.draw('prompt');
    return id ? this.deps.lookup(id) : undefined;
  }

  /** Writing phase: give up on a written prompt and play the one dealt as a fallback. */
  private useDealtPrompt(notice: string | null): void {
    const round = this.state.round;
    if (this.state.phase !== 'writing' || !round) return;
    round.notice = notice;
    this.beginSubmitting();
    this.touch();
  }

  private requireWriter(playerId: string): RoundState {
    const s = this.state;
    const round = s.round;
    if (s.phase !== 'writing' || !round) throw new GameError('It is not time to write a prompt.');
    if (round.writerId !== playerId) throw new GameError('Someone else is writing this one.');
    return round;
  }

  private canVoteFor(voterId: string, sub: SubmissionState): boolean {
    if (sub.playerId === voterId) return false;
    if (!teamsOn(this.state.settings)) return true;
    const voter = this.state.players.find((p) => p.id === voterId);
    const author = this.state.players.find((p) => p.id === sub.playerId);
    return !(voter?.team != null && author?.team === voter.team);
  }

  /** Points go to the player and, with teams on, to their team. */
  private award(playerId: string, points: number): void {
    const s = this.state;
    const p = s.players.find((x) => x.id === playerId);
    if (!p) return;
    p.score += points;
    if (teamsOn(s.settings) && p.team != null && s.teamScores) s.teamScores[p.team] = (s.teamScores[p.team] ?? 0) + points;
  }

  /** The team with the fewest players (first one on a tie), for newcomers. */
  private smallestTeam(exceptId?: string): number {
    const count = this.state.settings.teams?.length ?? 0;
    const sizes = Array.from({ length: count }, (_, i) => this.state.players.filter((p) => p.id !== exceptId && p.team === i).length);
    return sizes.indexOf(Math.min(...sizes));
  }

  /** Keep team seats valid after the teams change, and put anyone without a team on the smallest one. */
  private assignTeams(): void {
    const s = this.state;
    const count = teamsOn(s.settings) ? s.settings.teams!.length : 0;
    s.teamScores = Array.from({ length: count }, (_, i) => s.teamScores?.[i] ?? 0);
    for (const p of s.players) if (p.team == null || p.team >= count) p.team = null;
    if (!count) return;
    for (const p of s.players) if (p.team == null) p.team = this.smallestTeam(p.id);
  }

  private nextJudge(previous: string | null): string | null {
    const players = this.state.players;
    if (players.length === 0) return null;
    const start = previous ? players.findIndex((p) => p.id === previous) : -1;
    for (let i = 1; i <= players.length; i++) {
      const candidate = players[(start + i + players.length) % players.length];
      if (candidate.connected) return candidate.id;
    }
    return players[0].id;
  }

  /** Players the round is waiting on in the current phase. */
  private pendingPlayers(): PlayerState[] {
    const s = this.state;
    const round = s.round;
    if (!round) return [];
    if (s.phase === 'submitting') {
      return this.connectedPlayers().filter(
        (p) => p.id !== round.judgeId && !round.submissions.some((x) => x.playerId === p.id),
      );
    }
    if (s.phase === 'judging' && s.settings.judgeMode === 'vote') {
      return this.connectedPlayers().filter(
        (p) =>
          round.submissions.some((x) => this.canVoteFor(p.id, x)) && !round.submissions.some((x) => x.votes.includes(p.id)),
      );
    }
    if (s.phase === 'guessing') {
      const guesses = round.guesses ?? {};
      return this.connectedPlayers().filter((p) => round.guessers?.includes(p.id) && !guesses[p.id]);
    }
    return [];
  }

  private advanceIfReady(): void {
    const s = this.state;
    const round = s.round;
    if (!round) return;
    if (s.phase === 'submitting' && round.submissions.length > 0 && this.pendingPlayers().length === 0) {
      this.toJudging();
    } else if (s.phase === 'judging' && s.settings.judgeMode === 'vote' && this.pendingPlayers().length === 0) {
      this.tallyVotes();
    } else if (s.phase === 'guessing' && this.pendingPlayers().length === 0) {
      this.settleRound();
    }
  }

  private toJudging(): void {
    const s = this.state;
    const round = s.round!;
    if (round.submissions.length === 0) {
      this.skipRound('Nobody played a card in time, so here is a new prompt.', { keepJudge: true });
      return;
    }
    round.submissions = this.shuffle(round.submissions);
    const voting = s.settings.judgeMode === 'vote';
    for (const sub of round.submissions) sub.revealed = voting;
    round.deadline = voting && s.settings.roundTimer > 0 ? this.now() + s.settings.roundTimer * 1000 : null;
    s.phase = 'judging';
    if (voting && round.submissions.length === 1) {
      this.finishRound([round.submissions[0].id]);
      return;
    }
    // Nobody online can vote (say, only one team is left): count what there is instead of hanging.
    if (voting && this.pendingPlayers().length === 0) {
      this.tallyVotes();
      return;
    }
    this.touch();
  }

  private tallyVotes(): void {
    const round = this.state.round!;
    const top = Math.max(0, ...round.submissions.map((x) => x.votes.length));
    const winners = top === 0 ? [] : round.submissions.filter((x) => x.votes.length === top).map((x) => x.id);
    this.finishRound(winners);
  }

  /** The winner is decided. With guess who on, everyone guesses the author before it is scored. */
  private finishRound(winnerSubmissionIds: string[]): void {
    const s = this.state;
    const round = s.round!;
    round.winnerSubmissionIds = winnerSubmissionIds;
    round.deadline = null;
    round.notice = winnerSubmissionIds.length === 0 ? 'No votes, no points. Tough crowd.' : null;
    for (const sub of round.submissions) sub.revealed = true;
    if (s.settings.guessWho && winnerSubmissionIds.length) {
      const authors = new Set(round.submissions.filter((x) => winnerSubmissionIds.includes(x.id)).map((x) => x.playerId));
      const suspects = round.submissions.map((x) => x.playerId);
      // Winners know it was them, and anyone left with a single name to pick would get a free point.
      round.guessers = s.players
        .filter((p) => !authors.has(p.id) && suspects.filter((id) => id !== p.id).length >= 2)
        .map((p) => p.id);
      round.guesses = {};
      if (round.guessers.length) {
        s.phase = 'guessing';
        round.deadline = this.timerDeadline(GUESS_SECONDS);
        if (this.pendingPlayers().length === 0) this.settleRound();
        else this.touch();
        return;
      }
    }
    this.settleRound();
  }

  /** Score the round (winner, then any correct guesses), record it, and check for a game winner. */
  private settleRound(): void {
    const s = this.state;
    const round = s.round!;
    round.deadline = null;
    const winners = round.submissions.filter((x) => round.winnerSubmissionIds.includes(x.id));
    for (const w of winners) this.award(w.playerId, 1);
    if (s.phase === 'guessing') {
      const authors = new Set(winners.map((w) => w.playerId));
      for (const [guesser, suspect] of Object.entries(round.guesses ?? {})) if (authors.has(suspect)) this.award(guesser, 1);
      round.guessesRevealed = true;
    }
    s.roundsPlayed += 1;
    if (winners.length) {
      const writer = round.prompt.writeIn ? s.players.find((p) => p.id === round.writerId) : undefined;
      s.recap.unshift({
        number: round.number,
        prompt: round.prompt,
        winners: winners.map((w) => ({ playerName: w.playerName, cards: w.cards })),
        ...(round.worst ? { worst: true } : {}),
        ...(writer ? { writtenBy: writer.name } : {}),
      });
      s.recap = s.recap.slice(0, RECAP_LIMIT);
    }
    const played = round.submissions.flatMap((x) => x.cards).filter((c) => !c.writeIn);
    this.events.push({
      type: 'round_finished',
      playedCardIds: [...(round.prompt.writeIn ? [] : [round.prompt.id]), ...played.map((c) => c.id)],
      // The worst card of a Sabse Bekaar round is not a "win" for the card stats.
      winningCardIds: round.worst ? [] : winners.flatMap((w) => w.cards).filter((c) => !c.writeIn).map((c) => c.id),
    });
    this.discardAnswers(played);
    this.discardPrompt(round.prompt);
    s.phase = 'round_over';
    if (this.reachedTarget()) {
      this.finishGame();
      return;
    }
    this.touch();
  }

  private reachedTarget(): boolean {
    const s = this.state;
    const target = s.settings.pointsToWin;
    if (target <= 0) return false;
    if (teamsOn(s.settings)) return (s.teamScores ?? []).some((t) => t >= target);
    return s.players.some((p) => p.score >= target);
  }

  private finishGame(): void {
    const s = this.state;
    s.phase = 'game_over';
    if (s.round) s.round.deadline = null;
    let winnerName: string | null;
    if (teamsOn(s.settings)) {
      const scores = s.teamScores ?? [];
      const top = Math.max(0, ...scores);
      winnerName = top > 0 ? s.settings.teams!.filter((_, i) => scores[i] === top).join(' & ') : null;
    } else {
      const ids = new Set(leaderIds(s));
      winnerName = s.players.filter((p) => ids.has(p.id)).map((p) => p.name).join(' & ') || null;
    }
    this.events.push({ type: 'game_over', winnerName });
    this.touch();
  }

  private skipRound(notice: string, opts: { keepJudge?: boolean } = {}): void {
    const s = this.state;
    const round = s.round;
    if (!round) return;
    this.returnSubmissions();
    this.discardPrompt(round.prompt);
    for (const c of round.promptOptions ?? []) if (c.id !== round.prompt.id) this.discardPrompt(c);
    round.promptOptions = null;
    try {
      this.startRound(opts.keepJudge ? round.judgeId : null);
      if (s.round) s.round.notice = notice;
    } catch (err) {
      // Not enough players left to continue: park the game on the round-over screen.
      s.phase = 'round_over';
      round.winnerSubmissionIds = [];
      round.submissions = [];
      round.deadline = null;
      round.notice = err instanceof GameError ? err.message : notice;
      this.touch();
    }
  }

  /** Put submitted cards back in their owners' hands (write-ins refund the blank). */
  private returnSubmissions(): void {
    const round = this.state.round;
    if (!round) return;
    for (const sub of round.submissions) {
      const owner = this.state.players.find((p) => p.id === sub.playerId);
      for (const card of sub.cards) {
        if (!owner) {
          if (!card.writeIn) this.discardAnswers([card]);
        } else if (card.writeIn) {
          owner.writeInsLeft += 1;
        } else {
          owner.hand.push(card);
        }
      }
    }
    round.submissions = [];
  }

  private refillHand(player: PlayerState): void {
    while (player.hand.length < this.state.settings.handSize) {
      const id = this.draw('answer');
      if (!id) break;
      const card = this.deps.lookup(id);
      if (card) player.hand.push(card);
    }
  }

  private draw(kind: 'prompt' | 'answer'): string | undefined {
    const deck = this.state.deck;
    if (!deck) return undefined;
    const pile = kind === 'prompt' ? 'prompts' : 'answers';
    const discard = kind === 'prompt' ? 'promptDiscard' : 'answerDiscard';
    for (let attempts = 0; attempts < 2; attempts++) {
      while (deck[pile].length) {
        const id = deck[pile].pop()!;
        if (this.deps.lookup(id)) return id;
      }
      if (!deck[discard].length) return undefined;
      deck[pile] = this.shuffle(deck[discard]);
      deck[discard] = [];
    }
    return undefined;
  }

  /** Deck prompts go back in the pile; prompts written by players just disappear. */
  private discardPrompt(card: CardData): void {
    if (!card.writeIn) this.state.deck?.promptDiscard.push(card.id);
  }

  private discardAnswers(cards: CardData[]): void {
    const deck = this.state.deck;
    if (!deck) return;
    for (const c of cards) if (!c.writeIn) deck.answerDiscard.push(c.id);
  }

  private requireHost(playerId: string): void {
    if (this.state.hostId !== playerId) throw new GameError('Only the host can do that.');
  }

  private requireJudging(playerId: string): RoundState {
    const s = this.state;
    if (s.phase !== 'judging' || !s.round || s.settings.judgeMode !== 'judge') throw new GameError('It is not time to judge.');
    if (s.round.judgeId !== playerId) throw new GameError('Only the judge can do that.');
    return s.round;
  }

  private hostPresent(): boolean {
    const host = this.state.players.find((p) => p.id === this.state.hostId);
    return Boolean(host?.connected);
  }

  private promoteHostIfAway(): void {
    if (this.hostPresent()) return;
    const next = this.connectedPlayers().sort((a, b) => a.joinedAt - b.joinedAt)[0];
    if (next) this.state.hostId = next.id;
    else if (!this.state.players.some((p) => p.id === this.state.hostId)) this.state.hostId = this.state.players[0]?.id ?? null;
  }

  private shuffle<T>(items: T[]): T[] {
    const a = [...items];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(this.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  }

  private touch(): void {
    this.state.updatedAt = this.now();
  }
}

export function validateName(raw: string): string {
  const name = cleanText(String(raw ?? '')).slice(0, NAME_MAX).trim();
  if (!name) throw new GameError('Enter a name first.');
  return name;
}

export function sanitizeSettings(input: RoomSettings): RoomSettings {
  const clampInt = (v: unknown, min: number, max: number, fallback: number) => {
    const n = Math.round(Number(v));
    return Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : fallback;
  };
  const oneOf = <T,>(v: T, allowed: readonly T[], fallback: T) => (allowed.includes(v) ? v : fallback);
  const packIds = Array.isArray(input.packIds) ? [...new Set(input.packIds.map(String))].slice(0, 20) : [];
  return {
    packIds,
    handSize: clampInt(input.handSize, SETTING_LIMITS.handSize.min, SETTING_LIMITS.handSize.max, DEFAULT_SETTINGS.handSize),
    pointsToWin: oneOf(Number(input.pointsToWin), SETTING_LIMITS.pointsToWin, DEFAULT_SETTINGS.pointsToWin),
    roundTimer: oneOf(Number(input.roundTimer), SETTING_LIMITS.roundTimer, DEFAULT_SETTINGS.roundTimer),
    judgeMode: input.judgeMode === 'vote' ? 'vote' : 'judge',
    writeIns: clampInt(input.writeIns, SETTING_LIMITS.writeIns.min, SETTING_LIMITS.writeIns.max, DEFAULT_SETTINGS.writeIns),
    redraws: clampInt(input.redraws, SETTING_LIMITS.redraws.min, SETTING_LIMITS.redraws.max, DEFAULT_SETTINGS.redraws),
    promptChoice: input.promptChoice === true,
    guessWho: input.guessWho === true,
    teams: sanitizeTeams(input.teams),
    writeOwnEvery: oneOf(Number(input.writeOwnEvery ?? 0), SETTING_LIMITS.writeOwnEvery, 0),
    worstEvery: oneOf(Number(input.worstEvery ?? 0), SETTING_LIMITS.worstEvery, 0),
  };
}

/** 2 to 4 distinct team names, or [] for teams off. Blank names get a default. */
function sanitizeTeams(input: unknown): string[] {
  if (!Array.isArray(input)) return [];
  const { min, max, nameMax } = SETTING_LIMITS.teams;
  const names: string[] = [];
  input.slice(0, max).forEach((raw, i) => {
    let name = cleanText(typeof raw === 'string' ? raw : '').slice(0, nameMax).trim();
    if (!name) name = DEFAULT_TEAM_NAMES[i] ?? `Team ${i + 1}`;
    if (names.some((n) => n.toLowerCase() === name.toLowerCase())) name = `${name.slice(0, nameMax - 2)} ${i + 1}`;
    names.push(name);
  });
  return names.length >= min ? names : [];
}
